import { FC, useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { FormControl, Autocomplete, Grid, Fade, Box } from '@mui/material';
import { Loader, ModalSaveSection, Modal, PhoneTextField, TextField } from '../../../../components';
import { yupPhoneValidation, yupEmailValdation, yupRequiredString } from '../../../../helpers';
import { deepEqual } from 'fast-equals';
import { Formik, Form } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { createPendingVendor, getTemplates, getContentLibraryDocuments } from '../../../../fetch';
import {
  IDocumentTemplateRes,
  IError,
  IDocumentTemplate,
  IContentLibraryDocumentRes,
  IContentLibraryDocument,
  CONTENT_PLACE_HOLDER_BLOCKS
} from '../../../../models';

interface IPendingVendorsModalProps {
  open: boolean;
  handleClose: (refresh?: boolean) => void;
}

const PendingVendorsModalSchema = Yup.object().shape({
  name: yupRequiredString(),
  phoneNumber: yupPhoneValidation().required('Required'),
  email: yupEmailValdation().required('Required'),
  firstName: yupRequiredString(),
  lastName: yupRequiredString(),
  templateIds: Yup.array().of(Yup.string()).min(1, 'Required').required('Required'),
  contentLibraries: Yup.array()
});

export const PendingVendorsModal: FC<IPendingVendorsModalProps> = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTemplate, setSelectedTemplate] = useState<IDocumentTemplate | null>(null);
  const [selectedExhibitATemplate, setSelectedExhibitATemplate] =
    useState<IContentLibraryDocument | null>(null);
  const [selectedExhibitCTemplate, setSelectedExhibitCTemplate] =
    useState<IContentLibraryDocument | null>(null);

  const { isLoading: isLoadingTemplates, data: templatesData } = useQuery<
    IDocumentTemplateRes,
    IError
  >(['templates'], () =>
    getTemplates({
      isGenericTemplate: true
    })
  );
  const templates = useMemo(() => templatesData?.records || [], [templatesData]);

  const {
    isLoading: isLoadingExhibitATemplates,
    data: exhibitATemplatesData,
    refetch: fetchExhibitATemplates
  } = useQuery<IContentLibraryDocumentRes, IError>(
    ['exhibitATemplates'],
    () =>
      getContentLibraryDocuments({
        contentPlaceHolderBlock: 'ScopeOfWork'
      }),
    {
      enabled: false
    }
  );
  const exhibitATemplates = useMemo(
    () => exhibitATemplatesData?.records || [],
    [exhibitATemplatesData]
  );

  const {
    isLoading: isLoadingExhibitCTemplates,
    data: exhibitCTemplatesData,
    refetch: fetchExhibitCTemplates
  } = useQuery<IContentLibraryDocumentRes, IError>(
    ['exhibitCTemplates'],
    () =>
      getContentLibraryDocuments({
        contentPlaceHolderBlock: 'PriceSheet'
      }),
    {
      enabled: false
    }
  );
  const exhibitCTemplates = useMemo(
    () => exhibitCTemplatesData?.records || [],
    [exhibitCTemplatesData]
  );

  useEffect(() => {
    let mounted = true;
    if (mounted && selectedTemplate?.contentPlaceHolderBlocks?.includes('ScopeOfWork')) {
      fetchExhibitATemplates();
    }
    if (mounted && selectedTemplate?.contentPlaceHolderBlocks?.includes('PriceSheet')) {
      fetchExhibitCTemplates();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        name: '',
        phoneNumber: '',
        email: '',
        firstName: '',
        lastName: '',
        templateIds: [],
        contentLibraries: []
      }}
      validationSchema={PendingVendorsModalSchema}
      onSubmit={async (values, actions) => {
        try {
          const res = await createPendingVendor(values);
          if (res?.[0]?.error) {
            enqueueSnackbar(`${res?.[0]?.error}`, {
              variant: 'warning'
            });
          }
          enqueueSnackbar(`Pending vendor added!`, {
            variant: 'success'
          });
          handleClose(true);
          setSelectedTemplate(null);
          setSelectedExhibitATemplate(null);
          setSelectedExhibitCTemplate(null);
          actions.resetForm();
        } catch (error) {
          enqueueSnackbar(error?.Detail ?? `Error saving pending vendor, please try again`, {
            variant: 'error'
          });
        }
      }}
    >
      {({
        initialValues,
        isSubmitting,
        values,
        handleSubmit,
        dirty,
        isValid,
        setFieldValue,
        resetForm,
        errors
      }) => {
        const handleCancel = () => {
          if (!deepEqual(initialValues, values)) {
            const result = window.confirm(
              'You have unsaved changes, are you sure you want to exit?'
            );
            if (result) {
              handleClose();
              resetForm();
            } else {
              return;
            }
          } else {
            handleClose();
            resetForm();
          }
          setSelectedTemplate(null);
          setSelectedExhibitATemplate(null);
          setSelectedExhibitCTemplate(null);
        };
        return (
          <Form onSubmit={handleSubmit} autoComplete="none">
            <Modal open={open} onClose={handleCancel} maxWidth="sm" title="Add Pending Vendor">
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <div>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField fullWidth required label="Vendor Name" name="name" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField required label="Contact First Name" name="firstName" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField required label="Contact Last Name" name="lastName" />
                      </Grid>
                      <Grid item xs={12}>
                        <PhoneTextField
                          fullWidth
                          required
                          label="Phone Number"
                          name="phoneNumber"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField name="email" label="Email" required />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined" required>
                          <Autocomplete
                            value={selectedTemplate}
                            onChange={(event, newValue: IDocumentTemplate) => {
                              if (newValue && newValue?.name?.length > 0) {
                                setSelectedTemplate(newValue);
                                setFieldValue('templateIds', [newValue.pandaDocTemplateId]);
                              } else {
                                setSelectedTemplate(null);
                                setFieldValue('templateIds', []);
                              }
                            }}
                            id="templateIds"
                            options={templates}
                            disabled={isLoadingTemplates}
                            getOptionLabel={(option: IDocumentTemplate) => {
                              return option.name;
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                size="small"
                                name="templateIds"
                                autoComplete="none"
                                label="Template"
                                variant="outlined"
                                required
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      {selectedTemplate?.contentPlaceHolderBlocks?.includes('ScopeOfWork') && (
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined" required>
                            <Autocomplete
                              value={selectedExhibitATemplate}
                              onChange={(event, newValue: IContentLibraryDocument) => {
                                if (newValue && newValue?.documentName?.length > 0) {
                                  setSelectedExhibitATemplate(newValue);
                                  setFieldValue('contentLibraries', [
                                    ...values.contentLibraries,
                                    {
                                      contentPlaceHolderBlock:
                                        'ScopeOfWork' as CONTENT_PLACE_HOLDER_BLOCKS,
                                      contentLibraryId: newValue.pandaDocContentLibraryDocumentId
                                    }
                                  ]);
                                } else {
                                  setSelectedExhibitATemplate(null);
                                  setFieldValue(
                                    'contentLibraries',
                                    values.contentLibraries.filter(
                                      l => l.contentPlaceHolderBlock !== 'ScopeOfWork'
                                    )
                                  );
                                }
                              }}
                              id="exhibitATemplateIds"
                              options={exhibitATemplates}
                              disabled={isLoadingExhibitATemplates}
                              getOptionLabel={(option: IContentLibraryDocument) => {
                                return option.documentName;
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  name="exhibitATemplateIds"
                                  autoComplete="none"
                                  label="Exhibit A Template"
                                  variant="outlined"
                                  required
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>
                      )}

                      {selectedTemplate?.contentPlaceHolderBlocks?.includes('PriceSheet') && (
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined" required>
                            <Autocomplete
                              value={selectedExhibitCTemplate}
                              onChange={(event, newValue: IContentLibraryDocument) => {
                                if (newValue && newValue?.documentName?.length > 0) {
                                  setSelectedExhibitCTemplate(newValue);
                                  setFieldValue('contentLibraries', [
                                    ...values.contentLibraries,
                                    {
                                      contentPlaceHolderBlock:
                                        'PriceSheet' as CONTENT_PLACE_HOLDER_BLOCKS,
                                      contentLibraryId: newValue.pandaDocContentLibraryDocumentId
                                    }
                                  ]);
                                } else {
                                  setSelectedExhibitCTemplate(null);
                                  setFieldValue(
                                    'contentLibraries',
                                    values.contentLibraries.filter(
                                      l => l.contentPlaceHolderBlock !== 'PriceSheet'
                                    )
                                  );
                                }
                              }}
                              id="exhibitCTemplateIds"
                              options={exhibitCTemplates}
                              disabled={isLoadingExhibitCTemplates}
                              getOptionLabel={(option: IContentLibraryDocument) => {
                                return option.documentName;
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  name="exhibitCTemplateIds"
                                  autoComplete="none"
                                  label="Exhibit C Template"
                                  variant="outlined"
                                  required
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                  <ModalSaveSection
                    handleCancel={handleCancel}
                    isSaveDisabled={
                      !dirty ||
                      isSubmitting ||
                      !isValid ||
                      (selectedTemplate &&
                        selectedTemplate?.contentPlaceHolderBlocks?.includes('ScopeOfWork') &&
                        !selectedExhibitATemplate) ||
                      (selectedTemplate &&
                        selectedTemplate?.contentPlaceHolderBlocks?.includes('PriceSheet') &&
                        !selectedExhibitCTemplate)
                    }
                    handleSave={handleSubmit}
                  />
                </div>
              </Fade>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
